import { round, uniq } from 'lodash';

export function round2(n) {
    const num = parseFloat(n);
    return Math.round((Number.isFinite(num) ? num : 0) * 100) / 100;
}
export function perTicketFee(totalFees, qty) {
    return qty > 0 ? totalFees / qty : 0;
}
export function toNumber(n, def = 0) {
    const v = parseFloat(n);
    return Number.isFinite(v) ? v : def;
}

/** Calculate diff (positive => extra charge, negative => refund) */
export function ticketCalc(uniqueTicket) {
    const oldPriceSum = uniqueTicket.existingTickets.reduce((sum, t) => sum + t.finalPrice, 0);
    // if the new qty is less than existingTickets.length, we just take the first x tickets
    let newPriceSum = uniqueTicket.existingTickets
        .slice(0, uniqueTicket.quantity)
        .reduce((sum, t) => sum + t.finalPrice, 0);
    if (uniqueTicket.quantity > uniqueTicket.existingTickets.length) {
        newPriceSum =
            (newPriceSum || 0) +
            (uniqueTicket.newFinalPrice || 0) *
                (uniqueTicket.quantity - uniqueTicket.existingTickets.length);
    }
    return {
        newPrice: round2(newPriceSum),
        oldPrice: round2(oldPriceSum),
        diff: round2(newPriceSum - oldPriceSum)
    };
}

export function getTicketModifications(uniqueTicket) {
    const add_tickets = [];
    const modify_tickets = [];

    //modify_tickets: [{ booking_item_ticket_id, new_quantity }]
    if (uniqueTicket.quantity < uniqueTicket.existingTickets.length) {
        const originalBookingItemQtys = {};
        uniqueTicket.existingTickets.forEach(t => {
            if (!originalBookingItemQtys[t.booking_item_ticket_id]) {
                originalBookingItemQtys[t.booking_item_ticket_id] = 0;
            }
            originalBookingItemQtys[t.booking_item_ticket_id] += 1;
        });
        const newBookingItemQtys = {};
        uniqueTicket.existingTickets.slice(0, uniqueTicket.quantity).forEach(t => {
            if (!newBookingItemQtys[t.booking_item_ticket_id]) {
                newBookingItemQtys[t.booking_item_ticket_id] = 0;
            }
            newBookingItemQtys[t.booking_item_ticket_id] += 1;
        });
        // now we can compare originalBookingItemQtys and newBookingItemQtys
        Object.keys(originalBookingItemQtys).forEach(bid => {
            const originalQty = originalBookingItemQtys[bid] || 0;
            const newQty = newBookingItemQtys[bid] || 0;
            if (originalQty !== newQty) {
                modify_tickets.push({
                    booking_item_ticket_id: parseInt(bid, 10),
                    new_quantity: newQty
                });
            }
        });
    }

    //add_tickets: [{ ticket_id, quantity }]
    if (uniqueTicket.quantity > uniqueTicket.existingTickets.length) {
        add_tickets.push({
            ticket_id: uniqueTicket.ticketId,
            quantity: uniqueTicket.quantity - uniqueTicket.existingTickets.length
        });
    }
    return { add: add_tickets, modify: modify_tickets };
}

/*
    Unique tickets structure (list of tickets):
    {
        key: string; (ticket id currently)
        ticketId: number; (ticket id)
        name: string;
        newFinalPrice: number; (for new tickets, the price to use, or null if cannot be bought)
        quantity: number; // we do grab items from the existing tickets first, then from the possible tickets
        existingTickets:[   // flattened out from the original existingTickets, sorted by price asc, so we take the first x
            {
            booking_item_ticket_id: number;
            finalPrice: number; (retail + fees)
            }
        ],
    }}
*/

export function buildUniqueTickets(existingTickets, possibleTickets, uniqueTicketsValues) {
    const mapUniqueTicket = {};

    // do the existings first, but filter out discount tickets (they have discount_id and no ticket_id)
    existingTickets
        ?.filter(t => t.ticket_id !== null && !t.discount_id)
        ?.forEach(t => {
            // if we need a different booking just modify the key here
            const key = `${t.ticket_id}`;
            if (!mapUniqueTicket[key]) {
                mapUniqueTicket[key] = {
                    key,
                    ticketId: t.ticket_id,
                    name: t.name,
                    newFinalPrice: null,
                    quantity: 0,
                    existingTickets: []
                };
            }
            for (let q = 0; q < t.quantity; q++) {
                mapUniqueTicket[key].existingTickets.push({
                    booking_item_ticket_id: t.booking_item_ticket_id,
                    finalPrice: round2(
                        (t.retail_price || 0) + perTicketFee(t.taxes_and_fees || 0, t.quantity || 0)
                    )
                });
            }
            mapUniqueTicket[key].quantity += t.quantity || 0;
        });

    // sort each existingTickets key inside mapUniqueTicket by price asc (to leave the cheapest at the end)
    Object.values(mapUniqueTicket).forEach(u => {
        u.existingTickets.sort((a, b) => b.finalPrice - a.finalPrice);
    });

    // let's add the new tickets on top
    possibleTickets?.forEach(t => {
        const key = `${t.id}`;
        if (!mapUniqueTicket[key]) {
            mapUniqueTicket[key] = {
                key,
                ticketId: t.id,
                name: t.name,
                newFinalPrice: round2(t.finalPrice || 0),
                quantity: 0,
                existingTickets: []
            };
        } else {
            // if we already have it, just update the price if not set
            if (mapUniqueTicket[key].newFinalPrice === null) {
                mapUniqueTicket[key].newFinalPrice = round2(t.finalPrice || 0);
            }
        }
    });

    //if there were uniqueTicketsValues (meaning this is an update for the form), we need to grab the quantities from there
    if (uniqueTicketsValues && Array.isArray(uniqueTicketsValues)) {
        uniqueTicketsValues.forEach(u => {
            if (mapUniqueTicket[u.key]) {
                mapUniqueTicket[u.key].quantity = u.quantity || 0;
            }
        });
    }

    return Object.values(mapUniqueTicket);
}

/**
 * Calculate the total price difference for booking item modifications
 * including ticket changes, manual refunds, and discount deletions
 * @param {Object} values - Form values containing uniqueTickets, delete_discounts, vendorRefund, agencyRefund, discounts
 * @returns {number} Total difference (positive = additional charge, negative = refund)
 */
export function calculateTotalDiff(values) {
    // Calculate ticket quantity changes
    const ticketDiff = (values?.uniqueTickets || []).reduce((sum, u) => {
        const { diff } = ticketCalc(u);
        return sum + diff;
    }, 0);

    let finalDiff = ticketDiff;

    // Handle deleted discounts (add back the discount amount)
    for (const discountId of values?.delete_discounts || []) {
        const d = values.discounts?.find(x => x.id === discountId);
        if (d?.amount) {
            finalDiff += Math.abs(d.amount);
        }
    }

    // Subtract manual refunds
    const vendorRefund = parseFloat(values?.vendorRefund || 0);
    const agencyRefund = parseFloat(values?.agencyRefund || 0);
    finalDiff -= vendorRefund + agencyRefund;

    return round2(finalDiff);
}
